<script setup lang="ts">
import {Head, router} from '@inertiajs/vue3'
import {ref} from "vue";
import {useRoute} from "@/composables/route";
import {route} from "ziggy-js";
import {useFlash} from "@/composables/flash";
import VListItemNav from "@/components/navigation/VListItemNav.vue";
import NavigationFooter from "@/components/navigation/NavigationFooter.vue";
import AppBar from "@/components/navigation/AppBar.vue";
import BroadcastBanner from "@/components/navigation/BroadcastBanner.vue";
import {useTitle} from "@/composables/title";
import FlashBanner from "@/components/navigation/FlashBanner.vue";
import {useTeam} from "@/composables/team";
import {useColors} from "@/composables/colors";
import {useAssessment} from "@/composables/assessments";
import OnboardingDialog from "@/components/dialogs/OnboardingDialog.vue";
import {useAuth} from "@/composables/auth";
import {useBreadcrumb} from "@/composables/breadcrumb";

const {user} = useAuth()
const {isRoute, isPath} = useRoute()
const {isVisible} = useFlash()
const {title} = useTitle()
const {latestAssessments} = useAssessment()
const {teams, canCreateAssessment} = useTeam()
const {randomColor} = useColors()
const { breadcrumbs } = useBreadcrumb();

const show = ref(true)
const isOnboardingDialogOpen = ref(!user.value.onboarding_completed)
</script>
<template>
    <Head :title="`${title} · Capacity Framework Portal`" />
    <v-layout>
        <app-bar
            v-model="show"
        />
        <v-main>
            <broadcast-banner />
            <flash-banner />
            <v-navigation-drawer
                v-model="show"
                mobile-breakpoint="md"
                color="#10161D"
                theme="customDarkTheme"
            >
                <v-list
                    nav
                >
                    <v-list-item-nav
                        icon="mdi-home-outline"
                        :active="isRoute('home')"
                        @click="router.visit(route('home'))"
                    >
                        Home
                    </v-list-item-nav>
                    <v-list-item-nav
                        icon="mdi-check-circle-outline"
                        :active="isRoute('assessments.index')"
                        @click="router.visit(route('assessments.index'))"
                    >
                        My Assessments
                    </v-list-item-nav>
                    <v-list-item-nav
                        icon="mdi-bookmark-outline"
                        :active="isRoute('capacity-frameworks.index')"
                        @click="router.visit(route('capacity-frameworks.index'))"
                    >
                        Capacity Frameworks
                    </v-list-item-nav>
                    <v-divider class="my-2" />
                    <v-list-subheader class="text-disabled">
                        Ongoing Assessments
                    </v-list-subheader>
                    <v-list-item
                        v-for="assessment in latestAssessments"
                        :key="assessment.id"
                        :active="isPath(`/assessments/${assessment.id}`)"
                        nav
                        density="compact"
                        rounded="lg"
                        @click="router.visit(route('assessments.show', {assessment: assessment.id}))"
                    >
                        <v-list-item-title>
                            {{ assessment.title }}
                        </v-list-item-title>
                    </v-list-item>
                    <v-list-item
                        v-if="canCreateAssessment"
                        nav
                        density="compact"
                        rounded="lg"
                        base-color="primary"
                        @click="router.visit(route('assessments.create'))"
                    >
                        <template #prepend>
                            <v-icon>
                                mdi-plus
                            </v-icon>
                        </template>
                        <v-list-item-title>
                            Start new assessment
                        </v-list-item-title>
                    </v-list-item>
                    <v-divider class="my-2" />
                    <v-list-subheader class="text-disabled">
                        Resources
                    </v-list-subheader>
                    <v-list-item
                        v-for="team in teams"
                        :key="team.id"
                        :active="isPath(`/teams/${team.id}`)"
                        nav
                        density="compact"
                        rounded="lg"
                        @click="router.visit(route('teams.show', {team: team.id}))"
                    >
                        <template #prepend>
                            <v-avatar
                                variant="flat"
                                size="12"
                                rounded
                                :color="randomColor(team.id)"
                            />
                        </template>
                        <v-list-item-title>
                            {{ team.short_name }}
                        </v-list-item-title>
                        <template #append>
                            <v-chip
                                size="x-small"
                                rounded="lg"
                                variant="tonal"
                            >
                                {{ team.users_count }}
                            </v-chip>
                        </template>
                    </v-list-item>
                    <v-list-item
                        nav
                        density="compact"
                        rounded="lg"
                        base-color="primary"
                        @click="router.visit(route('teams.create'))"
                    >
                        <template #prepend>
                            <v-icon>
                                mdi-plus
                            </v-icon>
                        </template>
                        <v-list-item-title>
                            Create new resource
                        </v-list-item-title>
                    </v-list-item>
                </v-list>
                <template #append>
                    <navigation-footer />
                </template>
            </v-navigation-drawer>
            <v-container
                class="fill-height mt-0"
                :class="isVisible ? 'pt-0' : ''"
                :fluid="true"
            >
                <v-breadcrumbs
                    :items="breadcrumbs"
                    class="text-caption pl-0 pt-0"
                />
                <slot />
            </v-container>
        </v-main>
    </v-layout>
    <onboarding-dialog
        v-model="isOnboardingDialogOpen"
    />
</template>
<style scoped>
.text-footer {
    font-size: 0.750rem;
    color: #6B7280;
}

:deep(.v-breadcrumbs-item--disabled) {
    color: black !important;
    font-weight: 600 !important;
    opacity: 1 !important;
}
</style>
